/* Service Worker registration with auto-update prompt.
 *
 * Pattern:
 *   1. Register on app boot.
 *   2. When a new SW is waiting (i.e. a new deploy is live), surface a
 *      sonner toast: "A new version is available · Reload" so the user
 *      gets the latest bundle without a hard refresh.
 *   3. Skip everything in dev (CRA HMR doesn't play nicely with SW).
 */
import { toast } from "sonner";

export function registerServiceWorker() {
  // Register in production builds, OR when REACT_APP_ENABLE_SW=1 is set in
  // .env (used by the Emergent preview so QA + Playwright can exercise the
  // offline queue + Background Sync handlers against a CRA dev server).
  const forceEnabled = process.env.REACT_APP_ENABLE_SW === "1";
  if (process.env.NODE_ENV !== "production" && !forceEnabled) return;
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
  if (typeof window === "undefined" || !window.isSecureContext) return;

  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/service-worker.js")
      .then((reg) => {
        // Detect updates pushed during the session
        reg.addEventListener("updatefound", () => {
          const newSW = reg.installing;
          if (!newSW) return;
          newSW.addEventListener("statechange", () => {
            if (newSW.state === "installed" && navigator.serviceWorker.controller) {
              toast("A new version of the Staff Portal is available", {
                description: "Reload to get the latest features and fixes.",
                duration: 10000,
                action: {
                  label: "Reload",
                  onClick: () => {
                    newSW.postMessage({ type: "SKIP_WAITING" });
                    window.location.reload();
                  },
                },
              });
            }
          });
        });
      })
      .catch((err) => {
        // Don't surface to user — registration failures are non-fatal
        // eslint-disable-next-line no-console
        console.warn("Service worker registration failed:", err);
      });

    // Reload once when the controlling SW changes (after SKIP_WAITING) — but
    // NEVER on the very first visit. A fresh install calls clients.claim(),
    // which fires controllerchange on a page that was never controlled;
    // reloading there wipes whatever the user was typing (it was throwing
    // people off the login form mid-password).
    const hadController = !!navigator.serviceWorker.controller;
    let refreshing = false;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (!hadController || refreshing) return;
      refreshing = true;
      window.location.reload();
    });

    // Allow the SW to deep-link the user when they click a push notification.
    navigator.serviceWorker.addEventListener("message", (event) => {
      if (event.data && event.data.type === "PUSH_NAVIGATE" && event.data.url) {
        try {
          window.location.assign(event.data.url);
        } catch (err) {
          // Cross-origin navigation can throw — silently fall back to
          // staying on the current page (the push has already been
          // delivered to the SW), but surface for diagnostics.
          // eslint-disable-next-line no-console -- intentional dev/operator diagnostic
          if (typeof console !== "undefined") console.warn("PWA push deep-link navigation blocked:", err?.message || err);
        }
      }
    });
  });
}

export function unregisterServiceWorker() {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
  navigator.serviceWorker.getRegistrations().then((regs) => regs.forEach((r) => r.unregister()));
}
